// Presentation vocabulary for executive reporting (APP-004 R3 UI-P8).
//
// Two boundaries live here so no screen invents its own. The eight report types
// are the backend's, and nothing may add a ninth. A format is offered only when
// the running deployment says it can render it, and one that cannot be rendered
// is shown disabled with the reason rather than hidden, because a governance
// team is entitled to know the capability exists and why it is unavailable.
import { Badge } from "@odingard/design-system";
import type { BadgeTone } from "@odingard/design-system";
import type { ReportFormat, ReportRunStatus, ReportTrigger, ReportType } from "@odingard/sdk-client";

const REPORT_TYPE: Record<ReportType, { label: string; description: string }> = {
  governance_dashboard: {
    label: "Governance dashboard",
    description: "The whole position: policies, coverage, evidence, exceptions, findings and readiness.",
  },
  executive_summary: {
    label: "Executive summary",
    description: "Headline ratios and the work waiting on a person, for a board or leadership pack.",
  },
  framework_coverage: {
    label: "Framework coverage",
    description: "Requirements mapped, verified and evidenced for every adopted framework version.",
  },
  evidence_aging: {
    label: "Evidence aging",
    description: "How old the approved evidence is and what expires next.",
  },
  exception_register: {
    label: "Exception register",
    description: "Active exceptions, their risk level and what is due for review.",
  },
  regulatory_exposure: {
    label: "Regulatory exposure",
    description: "Regulations tracked, impact items raised and what is still unverified.",
  },
  audit_readiness: {
    label: "Audit readiness",
    description: "Frozen readiness scores per audit alongside the findings behind them.",
  },
  remediation_status: {
    label: "Remediation status",
    description: "Remediation assignments by state, what is overdue and what is due soon.",
  },
};

export const REPORT_TYPES = Object.keys(REPORT_TYPE) as ReportType[];

export function reportTypeLabel(type: ReportType): string {
  return REPORT_TYPE[type]?.label ?? type;
}

export function reportTypeDescription(type: ReportType): string {
  return REPORT_TYPE[type]?.description ?? "";
}

const FORMAT: Record<ReportFormat, { label: string; description: string }> = {
  csv: { label: "CSV", description: "One file, a section per table. Opens in any spreadsheet." },
  manifest_json: {
    label: "Manifest JSON",
    description: "The measured payload exactly as it was generated. The machine readable export.",
  },
  deck_json: {
    label: "Deck JSON",
    description: "A slide oriented projection for building an executive deck.",
  },
  xlsx: { label: "XLSX", description: "Workbook export." },
  pdf: { label: "PDF", description: "Paginated document export." },
};

/** Every format in the contract, in the order the screens list them. */
export const ALL_REPORT_FORMATS: ReportFormat[] = ["csv", "manifest_json", "deck_json", "xlsx", "pdf"];

export function formatLabel(format: ReportFormat): string {
  return FORMAT[format]?.label ?? format;
}

export function formatDescription(format: ReportFormat): string {
  return FORMAT[format]?.description ?? "";
}

/** Why a format the contract names cannot be produced by this deployment. */
export const RENDERER_REQUIRED = "Requires the Studio Reporting binary renderer.";

const RUN_STATUS: Record<ReportRunStatus, { label: string; tone: BadgeTone }> = {
  running: { label: "Running", tone: "info" },
  succeeded: { label: "Succeeded", tone: "success" },
  failed: { label: "Failed", tone: "danger" },
};

export function RunStatusBadge({ status }: { status: ReportRunStatus }) {
  const it = RUN_STATUS[status] ?? { label: status, tone: "neutral" as BadgeTone };
  return <Badge tone={it.tone}>{it.label}</Badge>;
}

export function triggerLabel(trigger: ReportTrigger): string {
  return trigger === "scheduled" ? "Scheduled" : "Run by hand";
}

/** Milliseconds as something a person reads at a glance. */
export function duration(ms: number): string {
  if (ms < 1000) return `${ms} ms`;
  return `${(ms / 1000).toFixed(1)} s`;
}
